import * as THREE from 'three';
import { Spell, SpellConfig } from './Spell';

export class LightningBolt extends Spell {
  private boltSegments: THREE.Line[] = [];
  private glowSphere!: THREE.Mesh;
  private impactAnimationId: number | null = null;
  private boltUpdateTimer: number = 0;

  constructor(scene: THREE.Scene, startPos: THREE.Vector3, targetPos: THREE.Vector3, damage: number, isPlayerSpell: boolean = true) {
    const config: SpellConfig = {
      type: 'lightningBolt',
      damage,
      speed: 22,
      color: new THREE.Color(0xFFD700),
      particleCount: 30,
      chargeTime: 0.5
    };
    super(scene, startPos, targetPos, config, isPlayerSpell);
  }

  protected createSpellVisual(): void {
    // Central glowing sphere
    const glowGeometry = new THREE.SphereGeometry(0.25, 12, 12);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFFFAA,
      transparent: true,
      opacity: 0.9
    });
    this.glowSphere = new THREE.Mesh(glowGeometry, glowMaterial);
    this.mesh.add(this.glowSphere);

    // Outer glow
    const outerGlowGeometry = new THREE.SphereGeometry(0.45, 12, 12);
    const outerGlowMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFD700,
      transparent: true,
      opacity: 0.4
    });
    const outerGlow = new THREE.Mesh(outerGlowGeometry, outerGlowMaterial);
    this.mesh.add(outerGlow);

    // Create initial bolt segments radiating outward
    this.regenerateBolts();
  }

  /** Generate random lightning bolt segments around the core */
  private regenerateBolts(): void {
    // Remove old bolts
    this.boltSegments.forEach(bolt => {
      this.mesh.remove(bolt);
      bolt.geometry.dispose();
      (bolt.material as THREE.Material).dispose();
    });
    this.boltSegments = [];

    // Create 4-6 lightning tendrils
    const boltCount = 4 + Math.floor(Math.random() * 3);
    for (let b = 0; b < boltCount; b++) {
      const points: THREE.Vector3[] = [];
      const angle = (b / boltCount) * Math.PI * 2 + Math.random() * 0.5;
      const length = 0.4 + Math.random() * 0.4;
      const segments = 4 + Math.floor(Math.random() * 3);

      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const x = Math.cos(angle) * length * t + (Math.random() - 0.5) * 0.15;
        const y = Math.sin(angle) * length * t + (Math.random() - 0.5) * 0.15;
        const z = (Math.random() - 0.5) * 0.15;
        points.push(new THREE.Vector3(x, y, z));
      }

      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.LineBasicMaterial({
        color: 0xFFFFFF,
        transparent: true,
        opacity: 0.8 + Math.random() * 0.2
      });
      const line = new THREE.Line(geometry, material);
      this.mesh.add(line);
      this.boltSegments.push(line);
    }
  }

  protected createParticles(): THREE.Points {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this.config.particleCount * 3);
    const colors = new Float32Array(this.config.particleCount * 3);

    for (let i = 0; i < this.config.particleCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const r = 0.2 + Math.random() * 0.3;

      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);

      // Yellow-white sparks
      colors[i * 3] = 1;
      colors[i * 3 + 1] = 0.85 + Math.random() * 0.15;
      colors[i * 3 + 2] = 0.3 + Math.random() * 0.7;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.08,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending
    });

    const particles = new THREE.Points(geometry, material);
    this.mesh.add(particles);
    return particles;
  }

  protected updateParticles(delta: number): void {
    if (!this.particles || !this.particles.geometry.attributes.position) return;

    const positions = this.particles.geometry.attributes.position.array as Float32Array;

    for (let i = 0; i < this.config.particleCount; i++) {
      // Sparks jitter rapidly
      positions[i * 3] += (Math.random() - 0.5) * 0.15;
      positions[i * 3 + 1] += (Math.random() - 0.5) * 0.15;
      positions[i * 3 + 2] += (Math.random() - 0.5) * 0.15;

      const dist = Math.sqrt(
        positions[i * 3] ** 2 +
        positions[i * 3 + 1] ** 2 +
        positions[i * 3 + 2] ** 2
      );

      if (dist > 0.5) {
        positions[i * 3] *= 0.4 / dist;
        positions[i * 3 + 1] *= 0.4 / dist;
        positions[i * 3 + 2] *= 0.4 / dist;
      }
    }

    this.particles.geometry.attributes.position.needsUpdate = true;

    // Regenerate bolt tendrils periodically for crackling effect
    this.boltUpdateTimer += delta;
    if (this.boltUpdateTimer > 0.08) {
      this.boltUpdateTimer = 0;
      this.regenerateBolts();
    }

    // Pulse the glow
    if (this.glowSphere) {
      this.glowSphere.scale.setScalar(1 + Math.sin(Date.now() * 0.02) * 0.2);
    }
  }

  public createImpactEffect(): void {
    const sparkCount = 120;
    const positions = new Float32Array(sparkCount * 3);
    const velocities: THREE.Vector3[] = [];
    const impactPos = this.impactPosition;

    for (let i = 0; i < sparkCount; i++) {
      positions[i * 3] = impactPos.x;
      positions[i * 3 + 1] = impactPos.y;
      positions[i * 3 + 2] = impactPos.z;

      velocities.push(new THREE.Vector3(
        (Math.random() - 0.5) * 12,
        (Math.random() - 0.5) * 12,
        (Math.random() - 0.5) * 12
      ));
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0xFFD700,
      size: 0.2,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending
    });

    const explosion = new THREE.Points(geometry, material);
    this.scene.add(explosion);

    // Add a bright flash
    const flashGeometry = new THREE.SphereGeometry(1.5, 16, 16);
    const flashMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFFFFF,
      transparent: true,
      opacity: 0.8
    });
    const flash = new THREE.Mesh(flashGeometry, flashMaterial);
    flash.position.copy(impactPos);
    this.scene.add(flash);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;

      const pos = explosion.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < sparkCount; i++) {
        pos[i * 3] += velocities[i].x * 0.016;
        pos[i * 3 + 1] += velocities[i].y * 0.016;
        pos[i * 3 + 2] += velocities[i].z * 0.016;
      }
      explosion.geometry.attributes.position.needsUpdate = true;

      material.opacity = 1 - (elapsed / 0.4);
      flashMaterial.opacity = 0.8 * (1 - elapsed / 0.15);
      flash.scale.setScalar(1 + elapsed * 8);

      if (elapsed < 0.4) {
        this.impactAnimationId = requestAnimationFrame(animate);
      } else {
        this.impactAnimationId = null;
        this.scene.remove(explosion);
        this.scene.remove(flash);
        geometry.dispose();
        material.dispose();
        flashGeometry.dispose();
        flashMaterial.dispose();
      }
    };

    this.impactAnimationId = requestAnimationFrame(animate);
  }

  dispose(): void {
    if (this.impactAnimationId !== null) {
      cancelAnimationFrame(this.impactAnimationId);
      this.impactAnimationId = null;
    }
    this.boltSegments.forEach(bolt => {
      bolt.geometry.dispose();
      (bolt.material as THREE.Material).dispose();
    });
    this.boltSegments = [];
    super.dispose();
  }
}
